import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useWorkspace } from "@/context/WorkspaceContext";
import { PageHeader, StatCard, EmptyState } from "@/components/PageBits";
import { Button } from "@/components/ui/button";
import { Radio, FolderKanban, Clock, Users, ArrowRight, BookOpen } from "lucide-react";

export default function Overview() {
  const { currentProject, currentOrg, projects, projectId } = useWorkspace();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [rooms, setRooms] = useState([]);

  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/usage`).then((r) => setUsage(r.data.summary)).catch(() => {});
    api.get(`/projects/${projectId}/rooms`).then((r) => setRooms(r.data)).catch(() => {});
  }, [projectId]);

  return (
    <div data-testid="overview-page">
      <PageHeader
        title="Overview"
        description={currentOrg ? `${currentOrg.name} · ${currentProject?.name || "no project"}` : "Loading workspace…"}
        actions={
          <Button data-testid="overview-quickstart" variant="outline"
            className="border-zinc-800 bg-zinc-950 hover:bg-zinc-900" onClick={() => navigate("/dashboard/quickstart")}>
            <BookOpen className="mr-2 h-4 w-4" /> Quick Start
          </Button>
        }
      />

      {!projectId ? (
        <EmptyState
          testId="overview-empty"
          title="Create your first project"
          description="Projects hold your rooms, API keys and usage. Create one to start integrating live media."
          action={<Button data-testid="overview-goto-projects" className="bg-zinc-50 text-zinc-900 hover:bg-zinc-200" onClick={() => navigate("/dashboard/projects")}>Go to Projects</Button>}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard testId="stat-projects" label="Projects" value={projects.length} icon={FolderKanban} hint={currentOrg?.name} />
            <StatCard testId="stat-active-rooms" label="Active Rooms" value={usage?.active_rooms ?? 0} icon={Radio} hint={`${rooms.length} total`} />
            <StatCard testId="stat-participants" label="Live Participants" value={usage?.active_participants ?? 0} icon={Users} hint={`${usage?.total_joins ?? 0} total joins`} />
            <StatCard testId="stat-minutes" label="Participant Minutes" value={usage?.participant_minutes ?? 0} icon={Clock} hint="metered via ledger" />
          </div>

          <div className="mt-8 rounded-md border border-zinc-800 bg-zinc-900/30">
            <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
              <h2 className="text-sm font-medium text-zinc-200">Recent rooms</h2>
              <button className="text-xs text-zinc-500 hover:text-zinc-300" data-testid="overview-view-rooms" onClick={() => navigate("/dashboard/rooms")}>View all</button>
            </div>
            {rooms.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-zinc-500">No rooms yet.</div>
            ) : (
              <ul className="divide-y divide-zinc-800">
                {rooms.slice(0, 5).map((r) => (
                  <li key={r.id} className="flex cursor-pointer items-center justify-between px-5 py-3 transition-colors hover:bg-zinc-900/50"
                    data-testid={`overview-room-${r.name}`} onClick={() => navigate(`/dashboard/rooms/${r.id}`)}>
                    <div className="flex items-center gap-3">
                      <span className={`h-2 w-2 rounded-full ${r.active_participants > 0 ? "bg-emerald-500 animate-pulse" : "bg-zinc-600"}`} />
                      <span className="font-mono text-sm text-zinc-200">{r.name}</span>
                      <span className="rounded border border-zinc-800 px-1.5 py-0.5 text-[10px] uppercase text-zinc-500">{r.type}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-zinc-500">
                      <span>{r.active_participants} live</span>
                      <ArrowRight className="h-3.5 w-3.5" />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
